/*
 * Kept in step with the backend's src/services/media/pcmFiles.ts (transcripts read
 * the same recordings on the server); a fix in one belongs in the other.
 */
/**
 * Uncompressed sound files from recorders and mixers: WAV (RIFF, and the
 * RF64/BW64 forms used past 4 GB) and AIFF/AIFC.
 *
 * Only the header is read here. The samples sit in one block, so the result
 * is an ordinary PCM AudioTrack whose frame table has a single chunk:
 * everything that reads PCM out of a camera file (by frame number, with
 * range requests) reads these the same way.
 */

import type { ByteSource } from './byteSource';
import { AudioTrack, FrameTable, MediaReadError, PcmCodec } from './isoAudio';

const HEADER_BLOCK_BYTES = 64 * 1024;
/** Chunks walked looking for the format and the data; real files have a handful. */
const MAX_CHUNKS = 1000;
const TWO_POW_32 = 4294967296;

const ascii4 = (data: Uint8Array, at: number): string =>
  String.fromCharCode(data[at], data[at + 1], data[at + 2], data[at + 3]);
const u16le = (data: Uint8Array, at: number) => data[at] | (data[at + 1] << 8);
const u32le = (data: Uint8Array, at: number) =>
  (data[at] | (data[at + 1] << 8) | (data[at + 2] << 16)) + data[at + 3] * 0x1000000;
const u64le = (data: Uint8Array, at: number) => u32le(data, at) + u32le(data, at + 4) * TWO_POW_32;
const u16be = (data: Uint8Array, at: number) => (data[at] << 8) | data[at + 1];
const u32be = (data: Uint8Array, at: number) =>
  data[at] * 0x1000000 + ((data[at + 1] << 16) | (data[at + 2] << 8) | data[at + 3]);

/** True for the first bytes of a WAV (RIFF, RF64 or BW64). */
export const isWavHeader = (head: Uint8Array): boolean =>
  head.length >= 12 && ['RIFF', 'RF64', 'BW64'].indexOf(ascii4(head, 0)) >= 0 && ascii4(head, 8) === 'WAVE';

/** True for the first bytes of an AIFF or AIFC. */
export const isAiffHeader = (head: Uint8Array): boolean =>
  head.length >= 12 && ascii4(head, 0) === 'FORM' && (ascii4(head, 8) === 'AIFF' || ascii4(head, 8) === 'AIFC');

const corrupt = (what: string) => new MediaReadError('corrupt', `The recording's ${what} could not be read.`);
const unsupported = (what: string) => new MediaReadError('unsupported-audio', what);

/** Reads `length` bytes at `offset` through a cached block, so walking chunk headers costs few requests. */
const blockReader = (source: ByteSource, signal?: AbortSignal) => {
  let block: Uint8Array | null = null;
  let blockStart = 0;
  return async (offset: number, length: number): Promise<Uint8Array> => {
    if (offset < 0 || offset + length > source.size) throw corrupt('header');
    if (!block || offset < blockStart || offset + length > blockStart + block.length) {
      blockStart = offset;
      block = await source.read(offset, Math.max(length, Math.min(HEADER_BLOCK_BYTES, source.size - offset)), signal);
      if (block.length < length) throw corrupt('header');
    }
    return block.subarray(offset - blockStart, offset - blockStart + length);
  };
};

const pcmTrack = (codec: PcmCodec, dataOffset: number, dataBytes: number): AudioTrack => {
  const frames = Math.floor(Math.max(0, dataBytes) / codec.bytesPerFrame);
  if (frames <= 0) throw new MediaReadError('no-audio', 'This recording has no sound in it.');
  const table: FrameTable = {
    kind: 'frames',
    count: 1,
    offsets: Float64Array.from([dataOffset]),
    firstFrames: Float64Array.from([0]),
    frameCounts: Float64Array.from([frames]),
    totalFrames: frames,
  };
  return {
    codec,
    timescale: codec.sampleRate,
    mediaStart: 0,
    startDelay: 0,
    table,
    fragmented: false,
    durationSeconds: frames / codec.sampleRate,
    complete: true,
  };
};

const checkRateAndChannels = (sampleRate: number, channels: number) => {
  if (!(channels >= 1 && channels <= 64)) throw unsupported('This recording has an unusual number of channels.');
  if (!(sampleRate >= 1000 && sampleRate <= 768000)) throw unsupported('This recording has an unusual sample rate.');
};

// ---------------------------------------------------------------------------
// WAV
// ---------------------------------------------------------------------------

const WAVE_FORMAT_PCM = 1;
const WAVE_FORMAT_IEEE_FLOAT = 3;
const WAVE_FORMAT_EXTENSIBLE = 0xfffe;

const wavCodec = (fmt: Uint8Array): PcmCodec => {
  if (fmt.length < 16) throw corrupt('format');
  let format = u16le(fmt, 0);
  const channels = u16le(fmt, 2);
  const sampleRate = u32le(fmt, 4);
  const blockAlign = u16le(fmt, 12);
  const bitsPerSample = u16le(fmt, 14);
  if (format === WAVE_FORMAT_EXTENSIBLE) {
    // The sub-format GUID starts with the real format tag.
    if (fmt.length < 40) throw corrupt('format');
    format = u16le(fmt, 24);
  }
  if (format !== WAVE_FORMAT_PCM && format !== WAVE_FORMAT_IEEE_FLOAT) {
    throw unsupported('This WAV is compressed (not PCM), so it can’t be read here. Export it as a PCM WAV.');
  }
  checkRateAndChannels(sampleRate, channels);
  // The container size decides the layout: 24-bit samples in 32-bit slots
  // (left-justified, as WAVE_FORMAT_EXTENSIBLE requires) read as 32-bit.
  const containerBytes = blockAlign > 0 && blockAlign % channels === 0 ? blockAlign / channels : Math.ceil(bitsPerSample / 8);
  const bits = containerBytes * 8;
  const float = format === WAVE_FORMAT_IEEE_FLOAT;
  if (float ? bits !== 32 && bits !== 64 : [8, 16, 24, 32].indexOf(bits) < 0) {
    throw unsupported(`${bitsPerSample}-bit ${float ? 'floating-point ' : ''}WAV files can’t be read here.`);
  }
  return {
    kind: 'pcm',
    bits: bits as PcmCodec['bits'],
    float,
    littleEndian: true,
    unsigned: bits === 8,
    sampleRate,
    channels,
    bytesPerFrame: containerBytes * channels,
  };
};

/**
 * The audio track of a WAV: RIFF, or RF64/BW64 where the 32-bit sizes are
 * replaced by the 64-bit ones in a `ds64` chunk. A data size that is missing
 * (0 or 0xFFFFFFFF, as streaming writers leave it) or runs past the end of
 * the file means "to the end of the file"; a plain RIFF over 4 GB whose size
 * wrapped around is recognised by the bytes left over being a multiple of 4 GiB.
 */
export const readWavTrack = async (source: ByteSource, signal?: AbortSignal): Promise<AudioTrack> => {
  const read = blockReader(source, signal);
  const head = await read(0, 12);
  if (!isWavHeader(head)) throw new MediaReadError('not-mp4', 'This file is not a WAV.');
  const form = ascii4(head, 0);
  let ds64DataSize: number | null = null;
  let codec: PcmCodec | null = null;
  let dataOffset = -1;
  let dataDeclared = 0;
  let offset = 12;
  for (let count = 0; count < MAX_CHUNKS && offset + 8 <= source.size; count += 1) {
    const header = await read(offset, 8);
    const id = ascii4(header, 0);
    const size = u32le(header, 4);
    const payload = offset + 8;
    if (id === 'ds64' && form !== 'RIFF') {
      const ds64 = await read(payload, Math.min(size, 28));
      if (ds64.length >= 16) ds64DataSize = u64le(ds64, 8);
    } else if (id === 'fmt ') {
      codec = wavCodec(await read(payload, Math.min(size, 64)));
    } else if (id === 'data') {
      dataOffset = payload;
      dataDeclared = size;
      if (codec) break;
    }
    // A data chunk of unknown or 64-bit size ends the walk; nothing useful can follow it.
    if (id === 'data' && (size === 0xffffffff || size === 0)) break;
    const length = id === 'data' && size === 0xffffffff && ds64DataSize !== null ? ds64DataSize : size;
    offset = payload + length + (length % 2);
  }
  if (!codec) throw corrupt('format');
  if (dataOffset < 0) throw new MediaReadError('no-audio', 'This recording has no sound in it.');
  const available = source.size - dataOffset;
  let dataBytes: number;
  if (form !== 'RIFF' && dataDeclared === 0xffffffff && ds64DataSize !== null) dataBytes = ds64DataSize;
  else if (dataDeclared === 0 || dataDeclared === 0xffffffff) dataBytes = available;
  else if (available > dataDeclared && (available - dataDeclared) % TWO_POW_32 === 0) dataBytes = available;
  else dataBytes = dataDeclared;
  return pcmTrack(codec, dataOffset, Math.min(dataBytes, available));
};

// ---------------------------------------------------------------------------
// AIFF / AIFC
// ---------------------------------------------------------------------------

/** An 80-bit IEEE 754 extended float (AIFF sample rates). */
const extended80 = (data: Uint8Array, at: number): number => {
  const exponent = u16be(data, at) & 0x7fff;
  const sign = data[at] & 0x80 ? -1 : 1;
  const high = u32be(data, at + 2);
  const low = u32be(data, at + 6);
  if (exponent === 0 && high === 0 && low === 0) return 0;
  return sign * (high * TWO_POW_32 + low) * Math.pow(2, exponent - 16383 - 63);
};

const AIFC_FORMATS: Record<string, { float: boolean; littleEndian: boolean; bits?: number; unsigned?: boolean }> = {
  NONE: { float: false, littleEndian: false },
  twos: { float: false, littleEndian: false },
  sowt: { float: false, littleEndian: true },
  in24: { float: false, littleEndian: false, bits: 24 },
  in32: { float: false, littleEndian: false, bits: 32 },
  fl32: { float: true, littleEndian: false, bits: 32 },
  FL32: { float: true, littleEndian: false, bits: 32 },
  fl64: { float: true, littleEndian: false, bits: 64 },
  FL64: { float: true, littleEndian: false, bits: 64 },
  'raw ': { float: false, littleEndian: false, bits: 8, unsigned: true },
};

/** The audio track of an AIFF or AIFC (uncompressed, or byte-swapped 'sowt', or float). */
export const readAiffTrack = async (source: ByteSource, signal?: AbortSignal): Promise<AudioTrack> => {
  const read = blockReader(source, signal);
  const head = await read(0, 12);
  if (!isAiffHeader(head)) throw new MediaReadError('not-mp4', 'This file is not an AIFF.');
  const aifc = ascii4(head, 8) === 'AIFC';
  let codec: PcmCodec | null = null;
  let frames = 0;
  let dataOffset = -1;
  let dataBytes = 0;
  let offset = 12;
  for (let count = 0; count < MAX_CHUNKS && offset + 8 <= source.size; count += 1) {
    const header = await read(offset, 8);
    const id = ascii4(header, 0);
    const size = u32be(header, 4);
    const payload = offset + 8;
    if (id === 'COMM') {
      const comm = await read(payload, Math.min(size, 64));
      if (comm.length < 18) throw corrupt('format');
      const channels = u16be(comm, 0);
      frames = u32be(comm, 2);
      const sampleSize = u16be(comm, 6);
      const sampleRate = Math.round(extended80(comm, 8));
      const compression = aifc && comm.length >= 22 ? ascii4(comm, 18) : 'NONE';
      const layout = AIFC_FORMATS[compression];
      if (!layout) throw unsupported('This AIFF is compressed, so it can’t be read here. Export it as an uncompressed AIFF or a WAV.');
      checkRateAndChannels(sampleRate, channels);
      const bits = layout.bits ?? Math.ceil(sampleSize / 8) * 8;
      if (layout.float ? bits !== 32 && bits !== 64 : [8, 16, 24, 32].indexOf(bits) < 0) {
        throw unsupported(`${sampleSize}-bit AIFF files can’t be read here.`);
      }
      codec = {
        kind: 'pcm',
        bits: bits as PcmCodec['bits'],
        float: layout.float,
        littleEndian: layout.littleEndian,
        unsigned: Boolean(layout.unsigned),
        sampleRate,
        channels,
        bytesPerFrame: (bits / 8) * channels,
      };
    } else if (id === 'SSND') {
      const ssnd = await read(payload, 8);
      dataOffset = payload + 8 + u32be(ssnd, 0);
      dataBytes = Math.max(0, size - 8 - u32be(ssnd, 0));
      if (codec) break;
    }
    offset = payload + size + (size % 2);
  }
  if (!codec) throw corrupt('format');
  if (dataOffset < 0) throw new MediaReadError('no-audio', 'This recording has no sound in it.');
  const available = Math.max(0, source.size - dataOffset);
  const bytes = Math.min(dataBytes || available, available, frames > 0 ? frames * codec.bytesPerFrame : Infinity);
  return pcmTrack(codec, dataOffset, bytes);
};
